/**
 * CFInput — F-002 Phase 4 (IMPL-P4-003).
 *
 * Codice Fiscale input with inline 300ms debounce validation. Wraps the
 * shadcn `<Input>` and adds:
 *  - trailing icon: `Loader2` (spinner) while debouncing,
 *                    `Check`   when CF is valid,
 *                    `X`       when CF is invalid.
 *  - `aria-live="polite"` reason text below the field so screen readers
 *    announce the result without stealing focus.
 *
 * Validation is a pure call to `parseCodiceFiscale()` from `@app/shared`,
 * optionally constrained by `cfType` ('PF' | 'PG'). No `lodash.debounce` —
 * `useEffect` + `setTimeout(300)` keeps the bundle minimal and matches the
 * codebase pattern already used by other inputs.
 *
 * Controlled component: parent owns the value via `value` + `onChange`.
 */
import { useEffect, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { parseCodiceFiscale, type CodiceFiscaleType } from '@app/shared';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';

type ValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid';

export interface CFInputProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  /** Restringe la validazione a PF (16 char) o PG (11 cifre). */
  cfType?: CodiceFiscaleType;
  /**
   * Errore esterno (es. da React Hook Form). Sovrascrive il messaggio inline
   * della validazione locale.
   */
  error?: string;
  id?: string;
}

export function CFInput({
  value,
  onChange,
  cfType,
  error,
  id,
  className,
  placeholder,
  ...rest
}: CFInputProps) {
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>('idle');
  const [validationReason, setValidationReason] = useState<string | undefined>();

  useEffect(() => {
    if (!value) {
      setValidationStatus('idle');
      setValidationReason(undefined);
      return;
    }

    setValidationStatus('validating');
    setValidationReason(undefined);

    const timer = setTimeout(() => {
      const parsed = parseCodiceFiscale(value);
      const matchesType = cfType ? parsed.type === cfType : true;
      if (parsed.valid && matchesType) {
        setValidationStatus('valid');
        setValidationReason(undefined);
      } else {
        setValidationStatus('invalid');
        setValidationReason(
          !matchesType && parsed.type
            ? `Atteso codice fiscale ${cfType}, ricevuto ${parsed.type}`
            : parsed.reason ?? 'Codice fiscale non valido',
        );
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [value, cfType]);

  const inlineMessage = error ?? (validationStatus === 'invalid' ? validationReason : undefined);

  return (
    <div className="space-y-1">
      <div className="relative">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          placeholder={placeholder ?? (cfType === 'PG' ? '01234567890' : 'RSSMRA85M01H501Z')}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-invalid={validationStatus === 'invalid' || Boolean(error)}
          className={cn('pr-9 uppercase', className)}
          {...rest}
        />
        <span
          className="pointer-events-none absolute inset-y-0 right-2 flex items-center"
          aria-hidden="true"
        >
          {validationStatus === 'validating' ? (
            <Loader2
              data-testid="icon-loader"
              className="h-4 w-4 animate-spin text-muted-foreground"
            />
          ) : validationStatus === 'valid' ? (
            <Check data-testid="icon-check" className="h-4 w-4 text-green-600" />
          ) : validationStatus === 'invalid' ? (
            <X data-testid="icon-x" className="h-4 w-4 text-destructive" />
          ) : null}
        </span>
      </div>

      <p
        role="status"
        aria-live="polite"
        className={cn(
          'min-h-[1rem] text-xs',
          inlineMessage ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {inlineMessage ?? (validationStatus === 'valid' ? 'Codice fiscale valido' : '')}
      </p>
    </div>
  );
}
